<template>
  <WizardFrame
    v-model:visible="visible"
    :steps="steps"
    :activeStep="stage"
    :submitLoading="submitLoading"
    :loading="loading"
    @onStepChanged="onStepChanged"
    @onCancel="onCancel"
    @onSubmit="saveModal"
  >
    <template #title>Edit an Event</template>
    <template #description>Make changes to an existing event</template>

    <template #body>
      <EventBasicsForm
        v-if="stage === 1 && newEvent"
        v-model:newEvent="newEvent"
        :isEditMode="true"
      />
      <EventDateTimeForm
        v-if="stage === 2"
        v-model:newEvent="newEvent"
        v-model:hasDateOverrides="hasDateOverrides"
        v-model:dateIncludes="dateIncludes"
        :isEdit="true"
      />
      <EventDeployment v-if="stage === 3" v-model:newEvent="newEvent" />
      <EventNotifications v-if="stage === 4" v-model:newEvent="newEvent" />
      <EventReview
        v-if="stage === 5"
        v-model:newEvent="newEvent"
        v-model:dateIncludes="dateIncludes"
        :showTitle="true"
      />
    </template>
  </WizardFrame>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import dayjs from 'dayjs';

import { getEventBooking, getEventById, getSensors, updateEvent } from '@/utils/api';

import WizardFrame from './WizardFrame.vue';
import EventBasicsForm from '@/components/events/forms/EventBasics.vue';
import EventDateTimeForm from '@/components/events/forms/EventDateTime.vue';
import EventDeployment from '@/components/events/forms/EventDeployment.vue';
import EventNotifications from '@/components/events/forms/EventNotifications.vue';
import EventReview from '@/components/events/forms/EventReview.vue';

import type { ClusterType, EventTypeEdit, EventTypeNew, ZoneType } from '@/types/Event';
import type { SensorIndexQuery } from '@/types/Sensor';
import type { AccountBooking } from '@/types/Booking';

import { useToastStore } from '@/utils/toast';
const toast = useToastStore();

import { useUserStore } from '@/stores/user';
const userStore = useUserStore();

import { useEventsStore } from '@/stores/events';
const eventsStore = useEventsStore();

const newEvent = ref<EventTypeNew>();

eventsStore.getEventListData();

const emit = defineEmits(['dataUpdated', 'onEventAdded', 'onEventSave', 'onEventClose']);

const eventId = defineModel<number | null>('eventId');
const submitLoading = ref(false);

onMounted(() => {
  loading.value = true;
  stage.value = 1;
  getEventDetails();
  getSensorCount();
});

const getEventDetails = () => {
  if (eventId.value) {
    getEventById(userStore.accessToken, eventId.value).then((resp) => {
      const savedEvent = resp['CampaignsEvent'];
      loading.value = false;

      type savedDate = {
        date: string;
        start: string;
        end: string;
        day?: string;
        from?: Date;
        to?: Date;
      };

      savedEvent.options = {
        hide_metrics: savedEvent.show_in_dashboard === 0,
        calibration: {
          engaged: savedEvent.multiplier_engaged,
          passerby: savedEvent.multiplier_passerby,
          other: savedEvent.multiplier_other,
        },
        cloning: {
          type: savedEvent.campaign_extra?.extra_data?.EventClone ?? 'None',
          format: savedEvent.campaign_extra?.extra_data?.CloneTitleStyle ?? '',
        },
      };

      if (savedEvent.campaign_extra?.extra_data) {
        const fixt = savedEvent.campaign_extra?.extra_data['sync:Fixtures'];
        if (fixt) {
          const fixtParsed = JSON.parse(fixt);
          savedEvent.options.removal_event = fixtParsed['RemovalEvent'];
        }
      }

      savedEvent.dates.map((date: savedDate) => {
        const initial = dayjs(date.date);
        const startTime = date.start.split(':');
        const endTime = date.end.split(':');

        const from = initial
          .set('hour', parseInt(startTime[0]))
          .set('minute', parseInt(startTime[1]));
        const to = initial.set('hour', parseInt(endTime[0])).set('minute', parseInt(endTime[1]));

        dateIncludes.value.push(initial.format('YYYY-MM-DD'));

        date.day = date.date;
        date.from = from.toDate();
        date.to = to.toDate();
        return date;
      });

      savedEvent.zones.map((zone: any) => {
        zone.sensor.engaged_signal = zone.range_engaged;
        zone.sensor.passerby_signal = zone.range_passerby;
        zone.sensors = [zone.sensor];
        return zone;
      });

      savedEvent.clusters.map((cluster: ClusterType) => {
        cluster.zones = [];

        savedEvent.zones.forEach((zone: ZoneType) => {
          if (zone.tags?.includes(cluster.name)) {
            cluster.zones.push(zone);
          }
        });

        return cluster;
      });

      if (savedEvent.booking_id) {
        savedEvent.canHaveBooking = true;
        getEventBooking(userStore.accessToken, savedEvent.account_id, savedEvent.id).then(
          (resp) => {
            savedEvent.booking = resp.data?.find(
              (booking: AccountBooking) => booking.id === savedEvent.booking_id,
            );
            newEvent.value = savedEvent;
            if (newEvent.value) {
              newEvent.value.type = savedEvent.event_type;
            }
            origEvent.value = { ...savedEvent };
          },
        );
      } else {
        newEvent.value = savedEvent;
        if (newEvent.value) {
          newEvent.value.type = savedEvent.event_type;
        }
        origEvent.value = { ...savedEvent };
      }
    });
  }
};

const visible = ref(true);
const loading = ref(true);
const hasSensors = ref(true);
const stage = ref(1);

const dateIncludes = ref<string[]>([]);

const hasDateOverrides = ref(true);

const origEvent = ref();

const saveModal = () => {
  submitLoading.value = true;
  const editedEvent = ref<EventTypeEdit>({});

  if (editedEvent.value) {
    if (newEvent.value?.name && origEvent.value.name !== newEvent.value?.name) {
      editedEvent.value.name = newEvent.value.name;
    }

    if (newEvent.value?.account_id && origEvent.value.account_id !== newEvent.value?.account_id) {
      editedEvent.value.account_id = newEvent.value.account_id;
    }

    if (
      newEvent.value?.timezone_id &&
      origEvent.value.timezone_id !== newEvent.value?.timezone_id
    ) {
      editedEvent.value.timezone_id = newEvent.value.timezone_id;
    }

    if (newEvent.value?.event_start && origEvent.value.event_start) {
      editedEvent.value.event_start = newEvent.value.event_start;
    }

    if (newEvent.value?.event_end && origEvent.value.event_end) {
      editedEvent.value.event_end = newEvent.value.event_end;
    }

    if (newEvent.value?.dates) {
      editedEvent.value.dates = [...newEvent.value.dates].map((date) => {
        const newDate = {
          day: date.day,
          date: date.date,
          start: date.start,
          end: date.end,
          from: dayjs(date.from).format('HH:mm'),
          to: dayjs(date.to).format('HH:mm'),
        };
        return newDate;
      });

      editedEvent.value.dates = editedEvent.value.dates.filter((date) => {
        return dateIncludes.value.includes(date.day);
      });
    }

    editedEvent.value.notifications = newEvent.value?.notifications;
    if (newEvent.value?.zones) {
      editedEvent.value.zones = newEvent.value?.zones;
    }
    if (newEvent.value?.clusters) {
      editedEvent.value.clusters = newEvent.value?.clusters;
    }

    if (editedEvent.value?.zones) {
      editedEvent.value.zones.map((zone) => {
        if (editedEvent.value.clusters) {
          editedEvent.value?.clusters.forEach((cluster) => {
            const clusterZones = cluster.zones.map((zone) => zone.name);
            if (clusterZones.includes(zone.name)) {
              if (!zone.clusters) {
                zone.clusters = [];
              }
              zone.clusters.push(cluster.name);
            }
          });
        }

        zone.sensors.map((sensor) => {
          sensor.mac = sensor.mac_address;
          sensor.label = zone.name;
          return sensor;
        });
        return zone;
      });
    }

    if (newEvent.value?.options) {
      editedEvent.value.options = newEvent.value.options;
    }

    if (newEvent.value?.verticals) {
      editedEvent.value.verticals = newEvent.value?.verticals;
    }

    if (newEvent.value?.type) {
      editedEvent.value.type = newEvent.value?.type;
    }

    if (newEvent.value?.options) {
      editedEvent.value.options = newEvent.value.options;
    }

    if (newEvent.value?.booking_id) {
      editedEvent.value.booking_id = newEvent.value.booking_id;
    }
  }

  if (eventId.value) {
    updateEvent(userStore.accessToken, eventId.value, editedEvent.value)
      .then((resp) => {
        submitLoading.value = false;
        if (toast.success(resp)) {
          visible.value = false;
          emit('onEventSave');
        }
      })
      .catch((error) => {
        submitLoading.value = false;
        toast.error(error);
      });
  }
};

const steps = computed(() => {
  return [
    {
      stepNumber: 1,
      stepName: 'Event',
      stepDescription: 'Describe your event',
      stepValid: stage1Valid.value,
    },
    {
      stepNumber: 2,
      stepName: 'Date and Time',
      stepDescription: 'Set date and time of event',
      stepValid: stage2Valid.value,
    },
    {
      stepNumber: 3,
      stepName: 'Deployment Configuration',
      stepDescription: 'Setup sensors',
      stepValid: stage3Valid.value,
    },
    {
      stepNumber: 4,
      stepName: 'Notifications',
      stepDescription: 'Manage notifications',
      stepValid: stage4Valid.value,
    },
    {
      stepNumber: 5,
      stepName: 'Review',
      stepDescription: '',
      stepValid: true,
    },
  ];
});

const stage1Valid = computed(() => {
  if (!newEvent.value) {
    return false;
  }

  if (newEvent.value.name.trim() === '') {
    return false;
  }
  if (!newEvent.value.timezone_id) {
    return false;
  }
  if (!newEvent.value.account_id) {
    return false;
  }
  if (!newEvent.value.type) {
    return false;
  }
  if (!newEvent.value.verticals.length) {
    return false;
  }

  return true;
});

const stage2Valid = computed(() => {
  if (newEvent.value?.event_start === '') {
    return false;
  }
  if (newEvent.value?.event_end === '') {
    return false;
  }

  if (newEvent.value?.booking) {
    const totalDays =
      dayjs(newEvent.value?.event_end)
        .add(1, 'day')
        .diff(dayjs(newEvent.value?.event_start), 'day') || 0;

    if (newEvent.value.booking.days && newEvent.value.booking.days < totalDays) {
      return false;
    }

    if (newEvent.value.booking.start_day && newEvent.value.booking.end_day) {
      const sameStart =
        dayjs(newEvent.value.booking.start_day).format('YYYY-MM-DD') ===
        dayjs(newEvent.value.event_start).format('YYYY-MM-DD');
      const sameEnd =
        dayjs(newEvent.value.booking.end_day).format('YYYY-MM-DD') ===
        dayjs(newEvent.value.event_end).format('YYYY-MM-DD');

      if (!sameStart || !sameEnd) {
        return false;
      }
    }
  }

  return true;
});

const stage3Valid = computed(() => {
  return true;
});

const stage4Valid = computed(() => {
  if (!newEvent.value?.notifications?.length) {
    return true;
  }

  let isValid = true;

  newEvent.value.notifications.forEach((note) => {
    if (note.email.trim() === '') {
      isValid = false;
    }
  });

  return isValid;
});

const onStepChanged = (stepNumber: number) => {
  stage.value = stepNumber;
};

const onCancel = () => {
  emit('onEventClose');
};

const getSensorCount = () => {
  loading.value = true;

  const params = ref<SensorIndexQuery>({
    page: 1,
    results_per_page: 10,
    search: '',
    sortDesc: false,
    sort: 'name',
    account: userStore.userData?.account_id,
  });

  getSensors(userStore.accessToken, params.value).then(
    (resp) => {
      loading.value = false;
      if (resp.total === 0) {
        hasSensors.value = false;
      }
    },
    (error) => {
      toast.error(error);
      loading.value = false;
      hasSensors.value = false;
    },
  );
};
</script>

<style lang="scss">
.p-dialog .p-dialog-confirm.p-dialog-confirm-cancel-add-event {
  svg {
    margin: variables.$vantage-base-sizing-xxxl variables.$vantage-base-sizing-sm
      variables.$vantage-base-sizing-xxs variables.$vantage-base-sizing-sm;
  }

  p {
    color: variables.$vantage-base-primitives-color-tertiary-400;
  }

  .modal-header {
    @include sizing.padding('xxs', 'sm');
    justify-content: center;
  }
}
</style>
